import { SimpleTable } from '@core/components/Table';
import { TableActionButton, TableActionButtonDelete, TableActionButtonEdit } from '@core/components/TableAction';
import TableActionContainer from '@core/components/TableAction/TableActionContainer';
import { useReduxLoading, useReduxMessage } from '@core/hooks';
import { permissaoUtils } from '@core/util/permissaoUtil';
import HandshakeSlashIcon from 'app/assets/icon/handshakeSlashIcon';
import moment from 'moment';
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ContratoAction, useContrato } from '../store';
import ContratoFab from './ContratoFab';
import { contratoTableStyles } from './styles';

const getCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value).replace('R$', '');

const ContratoTable = ({ onTour, onFetchData }) => {
  const [state, dispatch] = useContrato();

  const {
    message,
    page,
    selecionados,
    carregandoRegistro,
    carregandoRegistros,
    tipoSelecao,
    requestParams,
    filtroAvancado,
    somatorioFiltro,
  } = state;

  const styles = contratoTableStyles();

  const location = useLocation();

  useEffect(() => {
    async function editarContratoFaturamento() {
      const { contrato } = location;

      if (contrato !== undefined) {
        handleEditar(contrato);
      }
    }

    editarContratoFaturamento();
    // Efeito de montagem: roda uma única vez. Incluir as dependências reexecutaria a carga
    // inicial a cada render (loop de chamadas à API).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useReduxLoading(carregandoRegistro);

  useReduxMessage(message);

  const onSelectRow = (row) => {
    if (row && permissaoUtils.contrato().visualizar) {
      ContratoAction.requestVisualizar(dispatch, row.id);
    }
  };

  const getSomatorioContratosSelecionados = () => {
    let somatorio = 0;

    if (tipoSelecao === 'TODOS_FILTRO') {
      somatorio = somatorioFiltro;

      if (selecionados?.length > 0) {
        for (let i = 0; i < selecionados.length; i++) {
          somatorio -= selecionados[i].valor;
        }
      }
    } else if (selecionados?.length > 0) {
      for (let i = 0; i < selecionados.length; i++) {
        somatorio += selecionados[i].valor;
      }
    }

    return getCurrency(somatorio);
  };

  const onSelectionChange = (params) => dispatch(ContratoAction.setSelecionados(params));

  const onCurrentPageChange = (params) => dispatch(ContratoAction.setCurrentPage(params));

  const onPageSizeChange = (params) => dispatch(ContratoAction.setPageSize(params));

  const onSortingChange = (params) => dispatch(ContratoAction.setSorting(params));

  const handleEditar = (contrato) => ContratoAction.requestEditar(dispatch, contrato);

  const handleExcluir = (contrato) =>
    ContratoAction.requestExcluir(dispatch, [contrato.id], requestParams, tipoSelecao, filtroAvancado);

  const handleRescindirContrato = (contrato) => {
    ContratoAction.openModalRescisaoContrato(dispatch, contrato);
  };

  const getSituacaoTag = (situacao) => {
    if (situacao === 'ATIVO') {
      return (
        <div className={styles.situacaoContainer}>
          <div className={`${styles.badge} ${styles.badgeAtivo}`}>Ativo</div>
        </div>
      );
    }
    if (situacao === 'ENCERRADO') {
      return (
        <div className={styles.situacaoContainer}>
          <div className={`${styles.badge} ${styles.badgeEncerrado}`}>Encerrado</div>
        </div>
      );
    }
    if (situacao === 'RESCINDIDO') {
      return (
        <div className={styles.situacaoContainer}>
          <div className={`${styles.badge} ${styles.badgeRescindido}`}>Rescindido</div>
        </div>
      );
    }

    return '-';
  };

  const columns = [
    {
      name: 'identificador',
      title: 'Identificador',
      getCellValue: (contrato) => contrato?.identificador?.toString().padStart(3, '0'),
      sortingEnabled: true,
    },
    {
      name: 'cliente.pessoa.nome',
      title: 'Cliente',
      getCellValue: (contrato) => contrato?.nomeCliente,
      sortingEnabled: true,
      align: 'left',
    },
    {
      name: 'dataVigenciaInicial',
      title: 'Vigência do contrato',
      getCellValue: (contrato) =>
        contrato?.dataVigenciaInicial &&
        contrato?.dataVigenciaFinal &&
        `${moment(contrato.dataVigenciaInicial).format('DD/MM/YYYY')} a ${moment(contrato.dataVigenciaFinal).format(
          'DD/MM/YYYY'
        )}`,
      align: 'center',
      sortingEnabled: false,
    },
    {
      name: 'valor',
      title: 'Valor mensal (R$)',
      tooltipTitle: 'Valor mensal atual',
      getCellValue: (contrato) => contrato?.valor && getCurrency(contrato.valor),
      sortingEnabled: true,
      align: 'right',
      width: 160,
    },
    {
      name: 'situacao',
      title: 'Situação',
      getCellValue: (contrato) => getSituacaoTag(contrato?.situacao),
      sortingEnabled: false,
      hideTooltip: true,
      align: 'center',
    },
    {
      name: '',
      title: '',
      sortingEnabled: false,
      align: 'center',
      width: 125,
      getCellValue: (contrato) => (
        <>
          <TableActionContainer onTour={onTour}>
            {permissaoUtils.contrato().editar &&
              (contrato.situacao === 'ATIVO' || contrato.situacao === 'ENCERRADO') && (
                <TableActionButtonEdit className={'stepIconeEditar'} onClick={() => handleEditar(contrato)} />
              )}

            {permissaoUtils.contrato().excluir && contrato.situacao === 'ATIVO' && (
              <TableActionButtonDelete className={'stepIconeExcluir'} onClick={() => handleExcluir(contrato)} />
            )}

            {permissaoUtils.contrato().editar && contrato.situacao === 'ATIVO' && (
              <TableActionButton
                title="Rescindir contrato"
                icon={<HandshakeSlashIcon className={'stepIconeRescindir'} size={10} />}
                onClick={() => handleRescindirContrato(contrato)}
              />
            )}
          </TableActionContainer>
        </>
      ),
    },
  ];

  const hiddenColumnNames = [];

  // RNE-491
  if (filtroAvancado.situacao !== 'TODAS') {
    hiddenColumnNames.push('situacao');
  }

  const footerTable = (
    <div>
      <div style={{ padding: '12px 0 0 24px' }}>
        Valor total dos contratos (R$): {somatorioFiltro && getCurrency(somatorioFiltro)}
      </div>
      <div style={{ padding: '12px 0 0 24px' }}>
        Valor dos contratos selecionados (R$): {getSomatorioContratosSelecionados()}
      </div>
    </div>
  );

  return (
    <>
      <SimpleTable
        // fetchOnCreate
        defaultSorting={[{ columnName: 'cliente.pessoa.nome', direction: 'ASC' }]}
        getRowId={(row) => row}
        columns={columns}
        rows={page.content}
        selection={selecionados}
        loading={carregandoRegistros}
        totalCount={page.totalElements}
        searchBy={requestParams.q}
        fetchData={onFetchData}
        onRowClick={onSelectRow}
        onSelectionChange={onSelectionChange}
        onCurrentPageChange={onCurrentPageChange}
        onPageSizeChange={onPageSizeChange}
        onSortingChange={onSortingChange}
        selectionType={tipoSelecao}
        onSelectionTypeChange={(params) => {
          dispatch(ContratoAction.setTipoSelecao(params));
        }}
        footerTable={footerTable}
        hiddenColumnNames={hiddenColumnNames}
        params={filtroAvancado}
        contemFiltroAvancado
      />
      {(permissaoUtils.contrato().cadastrar || permissaoUtils.contrato().excluir) && <ContratoFab />}
    </>
  );
};

export default ContratoTable;
